import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AnalyticsDashboardTab from './employee_engagement/AnalyticsDashboardTab';
import FeedbackTab from './employee_engagement/FeedbackTab';
import RecognitionTab from './employee_engagement/RecognitionTab';
import TrainingTab from './employee_engagement/TrainingTab';

const EmployeeEngagementPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);

  // Feedback state
  const [surveys, setSurveys] = useState([]);
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [surveyResponse, setSurveyResponse] = useState({});
  const [pulseSurvey, setPulseSurvey] = useState(null);

  // Recognition state
  const [recognitions, setRecognitions] = useState([]);
  const [recognitionCategories, setRecognitionCategories] = useState([]);
  const [recognitionForm, setRecognitionForm] = useState({
    recipient_id: '',
    category_id: '',
    recognition_type: 'peer_to_peer',
    title: '',
    message: '',
    is_public: true
  });

  // Training state
  const [trainingResources, setTrainingResources] = useState([]);
  const [trainingCategories, setTrainingCategories] = useState([]);
  const [trainingFilters, setTrainingFilters] = useState({
    category_id: '',
    training_type: '',
    search: '',
    is_mandatory: null
  });
  const [userProgress, setUserProgress] = useState({});

  const fileInputRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    // Load initial data based on active tab
    if (activeTab === 'dashboard') {
      loadDashboardData();
    } else if (activeTab === 'feedback') {
      loadSurveys();
      loadActivePulseSurvey();
    } else if (activeTab === 'recognition') {
      loadRecognitions();
      loadRecognitionCategories();
    } else if (activeTab === 'training') {
      loadTrainingResources();
      loadTrainingCategories();
    }
  }, [activeTab]);

  // Dashboard functions
  const loadDashboardData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/employee-engagement/dashboard`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  // Feedback functions
  const loadSurveys = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/employee-engagement/surveys?include_questions=true`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSurveys(data);
      }
    } catch (error) {
      console.error('Error loading surveys:', error);
    }
  };

  const loadActivePulseSurvey = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/employee-engagement/pulse/active`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPulseSurvey(data);
      }
    } catch (error) {
      console.error('Error loading pulse survey:', error);
    }
  };

  const submitSurveyResponse = async (surveyId) => {
    setIsLoading(true);
    setError('');

    try {
      const responses = Object.entries(surveyResponse).map(([questionId, answer]) => ({
        question_id: questionId,
        response_value: typeof answer === 'string' ? answer : null,
        rating_value: typeof answer === 'number' ? answer : null,
        emoji_value: null // Could implement emoji responses
      }));

      const responseData = {
        survey_id: surveyId,
        is_anonymous: false,
        responses: responses
      };

      const response = await fetch(`${BACKEND_URL}/api/employee-engagement/surveys/${surveyId}/responses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(responseData)
      });

      if (response.ok) {
        setSuccess('Survey response submitted successfully!');
        setSurveyResponse({});
        setActiveSurvey(null);
        loadSurveys(); // Reload surveys
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to submit survey response');
      }
    } catch (error) {
      setError(`Error submitting survey: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const respondToPulse = async (emoji, comment = '') => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/employee-engagement/pulse/${pulseSurvey.id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          pulse_survey_id: pulseSurvey.id,
          emoji_response: emoji,
          comment: comment
        })
      });

      if (response.ok) {
        setSuccess('Pulse response submitted!');
        setPulseSurvey(null); // Hide pulse survey after response
      } else {
        setError('Failed to submit pulse response');
      }
    } catch (error) {
      setError(`Error submitting pulse: ${error.message}`);
    }
  };

  // Recognition functions
  const loadRecognitions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/employee-engagement/recognition?public_only=true&limit=20`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecognitions(data);
      }
    } catch (error) {
      console.error('Error loading recognitions:', error);
    }
  };

  const loadRecognitionCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/employee-engagement/recognition/categories`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecognitionCategories(data);
      }
    } catch (error) {
      console.error('Error loading recognition categories:', error);
    }
  };

  const submitRecognition = async () => {
    if (!recognitionForm.recipient_id || !recognitionForm.title || !recognitionForm.message) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/employee-engagement/recognition`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(recognitionForm)
      });

      if (response.ok) {
        setSuccess('Recognition submitted successfully!');
        setRecognitionForm({
          recipient_id: '',
          category_id: '',
          recognition_type: 'peer_to_peer',
          title: '',
          message: '',
          is_public: true
        });
        loadRecognitions(); // Reload recognitions
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to submit recognition');
      }
    } catch (error) {
      setError(`Error submitting recognition: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Training functions
  const loadTrainingResources = async () => {
    try {
      const params = new URLSearchParams();
      if (trainingFilters.category_id) params.append('category_id', trainingFilters.category_id);
      if (trainingFilters.training_type) params.append('training_type', trainingFilters.training_type);
      if (trainingFilters.search) params.append('search', trainingFilters.search);
      if (trainingFilters.is_mandatory !== null) params.append('is_mandatory', trainingFilters.is_mandatory);
      
      const response = await fetch(`${BACKEND_URL}/api/employee-engagement/training/resources?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrainingResources(data);
      }
    } catch (error) {
      console.error('Error loading training resources:', error);
    }
  };

  const loadTrainingCategories = async () => {
    try {
      // Note: This endpoint would need to be implemented
      setTrainingCategories([
        { id: '1', name: 'Safety Training', color: '#EF4444' },
        { id: '2', name: 'Technical Skills', color: '#3B82F6' },
        { id: '3', name: 'Leadership', color: '#10B981' },
        { id: '4', name: 'Compliance', color: '#F59E0B' }
      ]);
    } catch (error) {
      console.error('Error loading training categories:', error);
    }
  };

  const updateTrainingProgress = async (trainingId, progressData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/employee-engagement/training/${trainingId}/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(progressData)
      });

      if (response.ok) {
        const progress = await response.json();
        setUserProgress(prev => ({
          ...prev,
          [trainingId]: progress
        }));
        setSuccess('Progress updated!');
      }
    } catch (error) {
      setError(`Error updating progress: ${error.message}`);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Employee Engagement
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Feedback, recognition, and learning opportunities
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-600">{success}</p>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          {/* Mobile Tab Dropdown */}
          <div className="sm:hidden mb-4">
            <label htmlFor="tab-select" className="sr-only">Select a tab</label>
            <select
              id="tab-select"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="dashboard">📊 Analytics Dashboard</option>
              <option value="feedback">💬 Feedback & Surveys</option>
              <option value="recognition">🏆 Recognition Programs</option>
              <option value="training">📚 Training Library</option>
            </select>
          </div>

          {/* Desktop Tab Navigation */}
          <nav className="hidden sm:flex -mb-px overflow-x-auto scrollbar-hide">
            <div className="flex space-x-2 lg:space-x-8 min-w-max px-1">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`py-2 px-3 lg:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'dashboard'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <span className="hidden lg:inline">📊 Analytics Dashboard</span>
                <span className="lg:hidden">📊 Analytics</span>
              </button>
              <button
                onClick={() => setActiveTab('feedback')}
                className={`py-2 px-3 lg:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'feedback'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <span className="hidden lg:inline">💬 Feedback & Surveys</span>
                <span className="lg:hidden">💬 Feedback</span>
              </button>
              <button
                onClick={() => setActiveTab('recognition')}
                className={`py-2 px-3 lg:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'recognition'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <span className="hidden lg:inline">🏆 Recognition Programs</span>
                <span className="lg:hidden">🏆 Recognition</span>
              </button>
              <button
                onClick={() => setActiveTab('training')}
                className={`py-2 px-3 lg:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'training'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <span className="hidden lg:inline">📚 Training Library</span>
                <span className="lg:hidden">📚 Training</span>
              </button>
            </div>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-8">
          {activeTab === 'dashboard' && <AnalyticsDashboardTab dashboardData={dashboardData} />}
          {activeTab === 'feedback' && <FeedbackTab pulseSurvey={pulseSurvey} surveys={surveys} activeSurvey={activeSurvey} setActiveSurvey={setActiveSurvey} surveyResponse={surveyResponse} setSurveyResponse={setSurveyResponse} isLoading={isLoading} respondToPulse={respondToPulse} submitSurveyResponse={submitSurveyResponse} />}
          {activeTab === 'recognition' && <RecognitionTab recognitions={recognitions} recognitionCategories={recognitionCategories} recognitionForm={recognitionForm} setRecognitionForm={setRecognitionForm} isLoading={isLoading} submitRecognition={submitRecognition} />}
          {activeTab === 'training' && <TrainingTab trainingResources={trainingResources} trainingCategories={trainingCategories} trainingFilters={trainingFilters} setTrainingFilters={setTrainingFilters} loadTrainingResources={loadTrainingResources} updateTrainingProgress={updateTrainingProgress} />}
        </div>
      </div>
    </div>
  );
};

export default EmployeeEngagementPage;